import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Spinner}.
 *
 * All sizing and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports four sizes: sm (h-4 w-4), md (h-6 w-6, default), lg (h-8 w-8), xl (h-12 w-12)
 * - CSS animation (rotate)
 * - role="status" for accessibility
 *
 * @example
 * ```tsx
 * const classes = spinnerVariants({ size: 'md' })
 * // Returns: "inline-block animate-spin rounded-ds-full border-2 ... h-6 w-6"
 * ```
 */
export const spinnerVariants = cva(
	'inline-block animate-spin rounded-ds-full border-2 border-ds-border border-t-ds-primary',
	{
		variants: {
			size: {
				sm: 'h-4 w-4 border',
				md: 'h-6 w-6 border-2',
				lg: 'h-8 w-8 border-2',
				xl: 'h-12 w-12 border-[3px]',
			},
		},
		defaultVariants: { size: 'md' },
	},
)

/**
 * Props for the {@link Spinner} component.
 *
 * @property size - Spinner size: 'sm' (16) | 'md' (24, default) | 'lg' (32) | 'xl' (48)
 * @property className - Optional additional Tailwind classes
 * @property label - Accessible label for screen readers (default: "Loading…")
 *
 * @example
 * ```tsx
 * <Spinner size="md" label="Loading data..." />
 * ```
 */
export interface SpinnerProps
	extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
		VariantProps<typeof spinnerVariants> {
	label?: string
}

/**
 * Spinner component for loading states.
 *
 * @remarks
 * - **Styling:** All sizing and colors use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Sizes:** Small (h-4 w-4), medium (h-6 w-6, default), large (h-8 w-8), xl (h-12 w-12).
 * - **Animation:** CSS rotate animation (360deg continuous).
 * - **Accessibility:** `role="status"` with visually hidden label for screen readers.
 * - **Usage:** Loading indicators, async operations, data fetching.
 *
 * @example Basic usage
 * ```tsx
 * import { Spinner } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       {isLoading && <Spinner />}
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With custom label
 * ```tsx
 * <Spinner size="lg" label="Fetching user data..." />
 * ```
 *
 * @example Centered in container
 * ```tsx
 * <div className="flex items-center justify-center h-screen">
 *   <Spinner size="xl" />
 * </div>
 * ```
 *
 * @see {@link spinnerVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/spinner/spinner.stories.tsx for visual examples
 */
export function Spinner({ size, className, label = 'Loading…', ...props }: SpinnerProps) {
	return (
		<div {...props} role="status" className={cn(spinnerVariants({ size }), className)}>
			<span className="sr-only">{label}</span>
		</div>
	)
}
